/**
 * MechanicProductivityMonthly — month-wise drill-down powered by the
 * hours_engine single source of truth (same numbers as the Job Cards
 * "Show Break Down" tile).
 *
 * Per mechanic per day: clock in/out, workshop hours, allocated, completed,
 * produced. Clicking a day's "tasks" chip lists the tasks that mechanic
 * completed that day. CSV exports: month per-day, month completed tasks,
 * full-year summary.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { ChevronDown, ChevronRight, Download, CalendarDays, ClipboardList, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const fmtH = (v) => (v || 0).toFixed(1);

export default function MechanicProductivityMonthly() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [tasksFor, setTasksFor] = useState(null); // {mechanic_id, name, date}
  const [tasks, setTasks] = useState(null);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const yearOptions = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y -= 1) yearOptions.push(y);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/super-admin/productivity/monthly?year=${year}&month=${month}`,
        { credentials: 'include' },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      toast.error(`Could not load monthly productivity: ${e.message}`);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const openTasks = async (mid, name, date) => {
    setTasksFor({ mechanic_id: mid, name, date });
    setTasks(null);
    setTasksLoading(true);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/super-admin/productivity/day-tasks?date=${date}&mechanic_id=${encodeURIComponent(mid)}`,
        { credentials: 'include' },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setTasks(await r.json());
    } catch (e) {
      toast.error(`Could not load tasks: ${e.message}`);
    } finally {
      setTasksLoading(false);
    }
  };

  const downloadCsv = async (kind) => {
    setDownloading(kind);
    try {
      const qs = kind === 'year'
        ? `kind=year&year=${year}`
        : `kind=${kind}&year=${year}&month=${month}`;
      const r = await fetch(`${BACKEND_URL}/api/super-admin/productivity/export?${qs}`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const cd = r.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      const name = m ? m[1] : `mechanic_productivity_${kind}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${name}`);
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    } finally {
      setDownloading(null);
    }
  };

  const mechanics = data?.mechanics || [];
  const totals = data?.totals || {};

  return (
    <Card className="p-6" data-testid="sa-prod-monthly-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-emerald-600" />
            Monthly Productivity Drill-Down
            <Badge variant="outline" className="text-[10px] font-normal ml-1">hours engine</Badge>
          </h3>
          <p className="text-sm text-slate-500">
            Per-mechanic per-day clock in/out · workshop · allocated · completed · produced —
            same source of truth as the Job Cards breakdown tile.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm"
            data-testid="sa-prod-month-select"
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm"
            data-testid="sa-prod-year-select"
          >
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <Button size="sm" variant="outline" className="h-8 text-xs"
            onClick={() => downloadCsv('days')} disabled={!!downloading}
            data-testid="sa-prod-export-month">
            <Download className="h-3.5 w-3.5 mr-1" />
            {downloading === 'days' ? 'Exporting…' : 'Month CSV'}
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs"
            onClick={() => downloadCsv('tasks')} disabled={!!downloading}
            data-testid="sa-prod-export-tasks">
            <ClipboardList className="h-3.5 w-3.5 mr-1" />
            {downloading === 'tasks' ? 'Exporting…' : 'Tasks CSV'}
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs"
            onClick={() => downloadCsv('year')} disabled={!!downloading}
            data-testid="sa-prod-export-year">
            <Download className="h-3.5 w-3.5 mr-1" />
            {downloading === 'year' ? 'Exporting…' : `Year ${year} CSV`}
          </Button>
        </div>
      </div>

      {/* Month totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="sa-prod-totals">
        {[
          ['Workshop', totals.workshop_hours, 'text-slate-700'],
          ['Breaks', totals.break_hours, 'text-slate-500'],
          ['Allocated', totals.allocated_hours, 'text-blue-700'],
          ['Completed', totals.completed_hours, 'text-emerald-700'],
          ['Produced', totals.produced_hours, 'text-purple-700'],
        ].map(([label, val, cls]) => (
          <div key={label} className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2">
            <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
            <p className={`text-lg font-semibold ${cls}`}>{fmtH(val)}h</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-400 flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading {MONTHS[month - 1]} {year}…
        </div>
      ) : mechanics.length === 0 ? (
        <div className="py-10 text-center text-slate-400" data-testid="sa-prod-empty">
          No mechanic activity recorded for {MONTHS[month - 1]} {year}.
        </div>
      ) : (
        <div className="space-y-2">
          {mechanics.map((mech) => {
            const isOpen = !!expanded[mech.mechanic_id];
            return (
              <div key={mech.mechanic_id} className="rounded-lg border border-slate-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpanded((p) => ({ ...p, [mech.mechanic_id]: !p[mech.mechanic_id] }))}
                  className="w-full flex items-center justify-between px-3 py-2.5 bg-white hover:bg-slate-50 text-left"
                  data-testid={`sa-prod-mech-row-${mech.mechanic_id}`}
                >
                  <span className="flex items-center gap-2 font-medium text-slate-800">
                    {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                    {mech.name}
                    <span className="text-xs text-slate-400 font-normal">{mech.days_active} day{mech.days_active === 1 ? '' : 's'}</span>
                  </span>
                  <span className="flex items-center gap-3 text-xs">
                    <span className="text-slate-600">Workshop <strong>{fmtH(mech.totals.workshop_hours)}h</strong></span>
                    <span className="text-blue-700">Allocated <strong>{fmtH(mech.totals.allocated_hours)}h</strong></span>
                    <span className="text-emerald-700">Completed <strong>{fmtH(mech.totals.completed_hours)}h</strong></span>
                    <span className="text-purple-700">Produced <strong>{fmtH(mech.totals.produced_hours)}h</strong></span>
                  </span>
                </button>
                {isOpen && (
                  <div className="border-t border-slate-100 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-slate-50 text-slate-500">
                          <th className="px-3 py-1.5 text-left font-medium">Date</th>
                          <th className="px-2 py-1.5 text-center font-medium">Clock In</th>
                          <th className="px-2 py-1.5 text-center font-medium">Clock Out</th>
                          <th className="px-2 py-1.5 text-right font-medium">Workshop</th>
                          <th className="px-2 py-1.5 text-right font-medium">Breaks</th>
                          <th className="px-2 py-1.5 text-right font-medium">Allocated</th>
                          <th className="px-2 py-1.5 text-right font-medium">Completed</th>
                          <th className="px-2 py-1.5 text-right font-medium">Produced</th>
                          <th className="px-2 py-1.5" />
                        </tr>
                      </thead>
                      <tbody>
                        {mech.days.map((d) => (
                          <tr key={d.date} className="border-t border-slate-50 hover:bg-slate-50/60"
                            data-testid={`sa-prod-day-row-${mech.mechanic_id}-${d.date}`}>
                            <td className="px-3 py-1.5 font-mono text-slate-700">{d.date}</td>
                            <td className="px-2 py-1.5 text-center text-slate-600">{d.clock_in || '—'}</td>
                            <td className="px-2 py-1.5 text-center text-slate-600">{d.clock_out || '—'}</td>
                            <td className="px-2 py-1.5 text-right">{fmtH(d.workshop_hours)}</td>
                            <td className="px-2 py-1.5 text-right text-slate-400">{fmtH(d.break_hours)}</td>
                            <td className="px-2 py-1.5 text-right text-blue-700">{fmtH(d.allocated_hours)}</td>
                            <td className="px-2 py-1.5 text-right text-emerald-700 font-medium">{fmtH(d.completed_hours)}</td>
                            <td className="px-2 py-1.5 text-right text-purple-700">{fmtH(d.produced_hours)}</td>
                            <td className="px-2 py-1.5 text-right">
                              {d.completed_hours > 0 && mech.mechanic_id !== 'unassigned' && (
                                <button
                                  type="button"
                                  onClick={() => openTasks(mech.mechanic_id, mech.name, d.date)}
                                  className="text-emerald-700 underline underline-offset-2 hover:text-emerald-900"
                                  data-testid={`sa-prod-day-tasks-btn-${mech.mechanic_id}-${d.date}`}
                                >
                                  tasks
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Completed-tasks dialog */}
      <Dialog open={!!tasksFor} onOpenChange={(o) => { if (!o) { setTasksFor(null); setTasks(null); } }}>
        <DialogContent className="sm:max-w-2xl" data-testid="sa-prod-tasks-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-emerald-600" />
              Tasks completed — {tasksFor?.name}
            </DialogTitle>
            <DialogDescription>{tasksFor?.date}</DialogDescription>
          </DialogHeader>
          {tasksLoading ? (
            <div className="py-8 text-center text-slate-400 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !tasks || tasks.tasks.length === 0 ? (
            <div className="py-8 text-center text-slate-400">No completed tasks recorded on this day.</div>
          ) : (
            <div className="max-h-[50vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-slate-500 border-b">
                    <th className="py-1.5 text-left font-medium">Task</th>
                    <th className="py-1.5 text-left font-medium">Job Card</th>
                    <th className="py-1.5 text-left font-medium">Rego</th>
                    <th className="py-1.5 text-right font-medium">Hours</th>
                    <th className="py-1.5 text-right font-medium">At</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.tasks.map((t, i) => (
                    <tr key={`${t.job_card_id}-${i}`} className="border-b border-slate-50">
                      <td className="py-1.5 pr-2 text-slate-800">{t.description}</td>
                      <td className="py-1.5 pr-2 text-slate-500 font-mono text-xs">{t.job_card_number}</td>
                      <td className="py-1.5 pr-2 text-slate-500 font-mono text-xs">{t.rego || '—'}</td>
                      <td className="py-1.5 text-right font-medium text-emerald-700">{fmtH(t.hours)}</td>
                      <td className="py-1.5 text-right text-slate-500 text-xs">{t.completed_at || '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3} className="py-2 text-right text-xs text-slate-500 pr-2">Total completed</td>
                    <td className="py-2 text-right font-semibold text-emerald-700" data-testid="sa-prod-tasks-total">
                      {fmtH(tasks.total_hours)}h
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
